import { geometryGeoJsonToPolygon } from "@/app/(internal)/panel/(maps)/map/_utils/geometry-geojson-to-polygon";
import { buildBoundarySchoolAssignmentProps } from "@/providers/maps/utils/build-boundary-school-assignment.util";
import { resolvePreviewBoundaryDisplayColor } from "@/providers/maps/utils/get-boundary-polygon-preview-colors";
import type { IDistrictSchoolListItem } from "@/providers/store/api/districts/districts.interface";
import type {
  ISchoolBoundaryPreviewDetails,
  ISchoolBoundaryPreviewDetailsGrades,
  SchoolLevelType,
} from "@/providers/store/api/schools/schools.interface";
import { boundarySchoolLinksFromFeatureProperties } from "@/shared/utils/boundary-school-links.util";

import { propertyIdToString, readPreviewBoundaryFeatureProperties } from "./parse-preview-boundary-feature-ids";

export function getPreviewBoundaryId(feature: GeoJSON.Feature): string {
  const id = feature.id ?? feature.properties?.id;
  return String(id ?? "");
}

export function findDraftFeatureIndex(
  features: GeoJSON.Feature<GeoJSON.Polygon>[],
  boundaryId: string,
): number {
  return features.findIndex((feature) => {
    const id = getPreviewBoundaryId(feature);
    const props = feature.properties as Record<string, unknown> | undefined;
    const previewId =
      propertyIdToString(props?.previewId) ||
      propertyIdToString(props?.id) ||
      id;

    return id === boundaryId || previewId === boundaryId;
  });
}

export function previewDetailsFromFeature(
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
  fallbackDocumentId?: string,
): ISchoolBoundaryPreviewDetails | null {
  const id = getPreviewBoundaryId(feature);
  if (!id) {
    return null;
  }

  const props = feature.properties as Record<string, unknown> | undefined;
  const documentId = propertyIdToString(props?.documentId) || (fallbackDocumentId ?? "");
  if (!documentId) {
    return null;
  }

  const grades = props?.grades as ISchoolBoundaryPreviewDetailsGrades | undefined;

  return {
    id,
    documentId,
    schools: boundarySchoolLinksFromFeatureProperties(props),
    geometryGeoJson: JSON.stringify(feature.geometry),
    schoolTypes: (props?.schoolTypes as SchoolLevelType[] | undefined) ?? [],
    grades: grades ?? null,
    address: (props?.address as string | undefined) ?? null,
    color: (props?.color as string | undefined) ?? null,
  };
}

export function previewDetailsFromSchoolsUpdate(
  previous: ISchoolBoundaryPreviewDetails | null,
  feature: GeoJSON.Feature<GeoJSON.Polygon>,
  selectedSchools: readonly IDistrictSchoolListItem[],
): ISchoolBoundaryPreviewDetails | null {
  const base = previewDetailsFromFeature(feature) ?? previous;
  if (!base) {
    return null;
  }

  const assignment = buildBoundarySchoolAssignmentProps(selectedSchools);

  return {
    ...base,
    schools: assignment.schools,
    schoolTypes:
      assignment.schoolTypes.length > 0
        ? assignment.schoolTypes
        : base.schoolTypes,
    grades: assignment.grades ?? null,
    address: assignment.address ?? null,
    color: assignment.color ?? base.color ?? null,
  };
}

export function isValidPreviewPolygonGeometry(
  geometry: GeoJSON.Geometry | undefined,
): geometry is GeoJSON.Polygon {
  return geometry?.type === "Polygon";
}

export function featureFromPreviewGeometry(params: {
  id: string;
  geometryGeoJson: string;
  documentId: string;
  baseFeature?: GeoJSON.Feature<GeoJSON.Polygon> | null;
}): GeoJSON.Feature<GeoJSON.Polygon> {
  const geometry = geometryGeoJsonToPolygon(params.geometryGeoJson);
  const props = (params.baseFeature?.properties ?? {}) as Record<string, unknown>;
  const nested = readPreviewBoundaryFeatureProperties(props);
  const existingColor = [props.color, nested.color].find(
    (value): value is string => typeof value === "string" && value.trim() !== "",
  )?.trim() ?? "";

  return {
    type: "Feature",
    id: params.id,
    geometry,
    properties: {
      ...props,
      id: params.id,
      previewId: params.id,
      documentId: propertyIdToString(nested.documentId) || params.documentId,
      color: existingColor || resolvePreviewBoundaryDisplayColor({
        schoolId: propertyIdToString(nested.schoolId) || null,
        color: existingColor || null,
      }),
    },
  };
}
